import { Injectable } from '@angular/core';
import { ScopeSequenceStandard } from '../graphql/scope-sequence-standards-response';
import { DomainStandard } from 'src/app/home/scope-sequence/models/domain-standards.model';
import { StandardSetSettings, StandardSetType } from 'src/app/home/scope-sequence/settings/standard-set-settings.constants';

@Injectable({
  providedIn: 'root'
})
export class DomainStandardService {

  constructor() { }

  public groupIntoDomainStandards(scopeSequenceStandards: ScopeSequenceStandard[]): DomainStandard[] {
    const initialEmptyArray: DomainStandard[] = [];

    const groupedStandards = scopeSequenceStandards.reduce((domainStandards, scopeSequenceStandard) => {
      const domainName = scopeSequenceStandard.domainName;
      
      const existingDomainStandard = domainStandards.find(ds => ds.domainName === domainName);
      
      if (existingDomainStandard) {
        existingDomainStandard.standardSets.push(scopeSequenceStandard);
        return domainStandards;
      }

      domainStandards.push({
        // First standard with this domainName wins when picking displayOrder.
        domainDisplayOrder: scopeSequenceStandard.domainDisplayOrder ? Number(scopeSequenceStandard.domainDisplayOrder) : Number.MAX_SAFE_INTEGER,
        domainName: domainName,
        standardSets: [scopeSequenceStandard],
        settings: undefined,
        standardSetType: ''
      });

      return domainStandards;
    }, initialEmptyArray);

    return groupedStandards.sort((a, b) => Number(a.domainDisplayOrder) - Number(b.domainDisplayOrder))
  }

  public setSettings(domainStandard: DomainStandard) {
    const standardSetType = domainStandard.domainName.toLowerCase().includes(StandardSetType.Envision.toLowerCase())
      ? StandardSetType.Envision
      : StandardSetType.Renaissance;
    
    domainStandard.settings = StandardSetSettings.find(setting => setting.standardSetType === standardSetType);
  }

  public sortStandardSetsCollection(domainStandard: DomainStandard) {
    domainStandard.standardSets.sort((a, b) => this.compareStandardNames(a.standardName, b.standardName));
  }

  private compareStandardNames(nameA: string, nameB: string): number {
    // Standard names can have an arbitrary number of period-separated segments.
    // Sort on these segments, leftmost segment first (loosest grain), rightmost segment last (finest grain). 
    const segmentsA = nameA?.split('.') || [];
    const segmentsB = nameB?.split('.') || [];

    const maxSegmentLength = Math.max(segmentsA.length, segmentsB.length);

    for (let i = 0; i < maxSegmentLength; i++) {
      const segmentA = segmentsA[i] || '';
      const segmentB = segmentsB[i] || '';

      const comparison = this.compareSegment(segmentA, segmentB);
      if (comparison !== 0) {
        return comparison;
      }
    }
    return 0;
  }

  private compareSegment(segmentA: string, segmentB: string): number {
    const numericValuesInA = this.extractNumericValue(segmentA);
    const numericValuesInB = this.extractNumericValue(segmentB);

    let sort = 0

    // In each segment, sort by any numbers in the segment first (1a comes before 2a). If numbers can be sorted, sort by them.
    if (numericValuesInA !== Number.MAX_SAFE_INTEGER && numericValuesInB !== Number.MAX_SAFE_INTEGER) {
      sort = numericValuesInA - numericValuesInB;
    }

    if (sort !== 0) {
      return sort;
    }

    // Otherwise, sort alphabetically.
    return segmentA.localeCompare(segmentB);
  }

  private extractNumericValue(domainName: string): number {
    const match = domainName?.match(/\d+/);
    return match ? parseInt(match[0], 10) : Number.MAX_SAFE_INTEGER;
  }
}
